import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const getLinePosition = (value: string, position: number) => {
  const before = value.slice(0, position);
  const lineIndex = before.split('\n').length - 1;
  const lineStart = before.lastIndexOf('\n') + 1;
  const column = position - lineStart;
  return { lineIndex, lineStart, column };
};

const getLabelLength = (lineIndex: number, templates: string[]) => templates[lineIndex]?.length ?? 0;

/** Keeps the label prefix on each line uneditable — same protection the old bulk textarea used. */
const handleProtectedKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, templates: string[]) => {
  const textarea = e.currentTarget;
  const { lineIndex, lineStart } = getLinePosition(textarea.value, textarea.selectionStart);
  const labelBoundary = lineStart + getLabelLength(lineIndex, templates);
  const isBeforeLabel = textarea.selectionStart < labelBoundary;

  const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowRight', 'Tab', 'PageUp', 'PageDown'];
  if (navigationKeys.includes(e.key)) return;

  if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
    if (textarea.selectionStart <= labelBoundary) {
      e.preventDefault();
      textarea.setSelectionRange(labelBoundary, labelBoundary);
    }
    return;
  }
  if (e.key === 'Home') {
    e.preventDefault();
    textarea.setSelectionRange(labelBoundary, labelBoundary);
    return;
  }
  if (e.key === 'Delete') {
    if (textarea.selectionStart < labelBoundary) {
      e.preventDefault();
      textarea.setSelectionRange(labelBoundary, labelBoundary);
    }
    return;
  }
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (e.key.length === 1 && isBeforeLabel) {
    e.preventDefault();
    textarea.setSelectionRange(labelBoundary, labelBoundary);
  }
};

const clampSelectionToValue = (textarea: HTMLTextAreaElement, templates: string[]) => {
  requestAnimationFrame(() => {
    const { lineIndex, lineStart, column } = getLinePosition(textarea.value, textarea.selectionStart);
    const labelLength = getLabelLength(lineIndex, templates);
    const labelBoundary = lineStart + labelLength;
    if (column < labelLength) {
      const selectionLength = textarea.selectionEnd - textarea.selectionStart;
      textarea.setSelectionRange(labelBoundary, Math.max(labelBoundary, labelBoundary + selectionLength));
    }
  });
};

/** Re-applies each line's label prefix after any edit, so pasted/typed text can never remove it. */
const ensureTemplateLabels = (text: string, templates: string[]): string => {
  const lines = text.split(/\r?\n/);
  return templates
    .map((template, idx) => {
      const line = lines[idx] ?? '';
      if (line.startsWith(template)) return line;
      const rawLabel = template.replace(/\t$/, '');
      if (line.startsWith(rawLabel)) {
        return `${template}${line.slice(rawLabel.length).trimStart()}`;
      }
      return `${template}${line.replace(/^[^\t]*\t?/, '').trimStart()}`;
    })
    .join('\n');
};

/**
 * Small paste-to-fill popup. Pre-filled with one label per row (BOL, AOL, 10,
 * 20…) exactly like the old always-visible bulk textarea — the label prefix
 * on each line is protected (can't be edited/deleted), so pasted or typed
 * values always land next to the correct row regardless of order, and the
 * caller can parse the result as plain "label<TAB>value" lines in row order.
 */
export const PastePopup: React.FC<{
  title: string;
  templates: string[];
  onClose: () => void;
  onApply: (text: string) => void;
}> = ({ title, templates, onClose, onApply }) => {
  const [text, setText] = useState(() => templates.join('\n'));

  const handleChange = (value: string) => {
    setText(ensureTemplateLabels(value, templates));
  };

  // Distribute a multi-line clipboard paste one value per template line,
  // instead of letting the browser insert it all at the cursor position.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;
    const values = pasted
      .split(/\r?\n/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    if (values.length === 0) return;

    e.preventDefault();

    const combined = templates.map((template, idx) => `${template}${values[idx] ?? ''}`);
    if (values.length > templates.length) {
      combined.push(...values.slice(templates.length));
    }
    setText(ensureTemplateLabels(combined.join('\n'), templates));
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">
          <p className="mb-2 text-xs text-gray-500">
            Each line is already labelled — paste your values so they line up in order.
          </p>
          <textarea
            autoFocus
            className="input h-64 w-full resize-none font-mono text-sm"
            value={text}
            onChange={(e) => handleChange(e.target.value)}
            onSelect={(e) => clampSelectionToValue(e.currentTarget, templates)}
            onClick={(e) => clampSelectionToValue(e.currentTarget, templates)}
            onKeyDown={(e) => handleProtectedKeyDown(e, templates)}
            onPaste={handlePaste}
          />
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-100 p-4">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => {
              onApply(text);
              onClose();
            }}
            className="btn-primary"
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
